import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Lightning, Sparkle, ArrowRight, CreditCard, Coins,
  Check, ShoppingCart, Rocket
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Popup types ─── */
const POPUPS = {
  soft_nudge: {
    title: 'You\'re getting great results',
    text: 'Unlock more content creation power and keep the momentum going.\n\nUpgrade to Growth for full access to all creation tools.',
    primary: 'Upgrade to Growth — $19/mo',
    secondary: 'Continue',
    target: 'growth',
  },
  hard_paywall: {
    title: 'You\'ve reached your current plan limit',
    text: 'Upgrade to continue creating content, generating ideas, and growing your brand without interruptions.',
    primary: 'See Plans',
    secondary: 'Maybe later',
    target: 'growth',
  },
  export_paywall: {
    title: 'Ready to download your content?',
    text: 'Upgrade to download and use your designs without watermarks.\n\nGrowth plan is just $19/month.',
    primary: 'Upgrade to Growth — $19/mo',
    secondary: null,
    target: 'growth',
  },
  locked_feature: {
    title: 'Upgrade to unlock this feature',
    text: 'Upgrade to unlock full access and take your content creation to the next level.',
    primary: 'See Plans',
    secondary: null,
    target: 'growth',
  },
  pro_upsell: {
    title: 'Unlock Video Ads & more',
    text: 'Upgrade to Pro for Video Ad Creator, higher usage limits, and full platform access.',
    primary: 'Upgrade to Pro — $29/mo',
    secondary: null,
    target: 'pro',
  },
  credits_exhausted: {
    title: 'Credits exhausted',
    text: 'Top up instantly to keep creating, or upgrade your plan for more monthly credits.',
    primary: 'Buy Credits',
    secondary: 'Upgrade Plan',
    target: 'credits',
  },
  post_export_nudge: {
    title: 'Content ready',
    text: 'Want to create more? Upgrade for more credits and full access to all tools.',
    primary: 'See Plans',
    secondary: 'Continue',
    target: 'growth',
  },
};

/* ─── Context ─── */
const UpgradeContext = createContext(null);

export function useUpgrade() {
  return useContext(UpgradeContext);
}

export function UpgradeProvider({ children }) {
  const { user } = useAuth();
  const [popup, setPopup] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [usageCount, setUsageCount] = useState(0);

  const tier = user?.subscription?.tier || 'starter';

  const showPopup = useCallback((type) => {
    if (!POPUPS[type]) return;
    setPopup(type);
  }, []);

  const dismiss = useCallback(() => setPopup(null), []);

  const trackUsage = useCallback(() => {
    setUsageCount(prev => {
      const next = prev + 1;
      if (tier === 'starter' && (next === 2 || next === 3) && !popup) {
        setPopup('soft_nudge');
      }
      return next;
    });
  }, [tier, popup]);

  const checkCredits = useCallback((cost) => {
    // Returns true if enough credits, shows popup if not
    return true; // Credit check happens server-side, popup triggered on 402/429
  }, []);

  const handleApiError = useCallback((err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    // Safely extract message string from detail (could be string or object)
    const detailMsg = typeof detail === 'string' ? detail : (detail?.message || '');

    if (status === 402) {
      // Insufficient credits
      setPopup('credits_exhausted');
      return true;
    }
    if (status === 429) {
      if (detailMsg.toLowerCase().includes('credit')) {
        setPopup('credits_exhausted');
      } else {
        setPopup('hard_paywall');
      }
      return true;
    }
    if (status === 403) {
      if (detail?.type === 'usage_limit_reached') {
        setPopup('hard_paywall');
      } else {
        setPopup('locked_feature');
      }
      return true;
    }
    return false;
  }, []);

  const showExportPaywall = useCallback((creditCost = 0) => {
    if (tier === 'starter') {
      setPopup('export_paywall');
      return true;
    }
    return false;
  }, [tier]);

  const showScaleUpsell = useCallback(() => {
    if (tier === 'growth') {
      setPopup('pro_upsell');
    }
  }, [tier]);

  const showCreditPurchase = useCallback(() => {
    setCreditModal(true);
    setPopup(null);
  }, []);

  const showPostExportNudge = useCallback(() => {
    if (tier === 'starter') {
      setPopup('post_export_nudge');
    }
  }, [tier]);

  const checkCreditsBeforeAction = useCallback(async (featureName, onSuccess) => {
    try {
      const token = localStorage.getItem('bf_access_token');
      await axios.post(`${API}/api/credits/check?feature=${featureName}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onSuccess) await onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        if (tier === 'starter') {
          setPopup('export_paywall');
        } else {
          setPopup('credits_exhausted');
        }
        return false;
      }
      if (status === 429) {
        setPopup('hard_paywall');
        return false;
      }
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Something went wrong');
      return false;
    }
    return true;
  }, [tier]);

  const deductCreditsAfterExport = useCallback(async (featureName) => {
    try {
      const token = localStorage.getItem('bf_access_token');
      await axios.post(`${API}/api/credits/deduct?feature=${featureName}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (tier === 'starter') {
        setTimeout(() => setPopup('post_export_nudge'), 1500);
      }
    } catch {
      // Deduction failed but export succeeded — don't block the user
    }
  }, [tier]);

  const value = {
    showPopup, dismiss, trackUsage, checkCredits, handleApiError,
    showExportPaywall, showScaleUpsell, showCreditPurchase,
    showPostExportNudge, checkCreditsBeforeAction, deductCreditsAfterExport,
    tier, creditModal, setCreditModal,
  };

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <UpgradePopup type={popup} onDismiss={dismiss} onCredits={showCreditPurchase} tier={tier} />
      <CreditPurchaseModal open={creditModal} onClose={() => setCreditModal(false)} />
    </UpgradeContext.Provider>
  );
}

/* ─── Upgrade Popup ─── */
function UpgradePopup({ type, onDismiss, onCredits, tier }) {
  const navigate = useNavigate();
  if (!type || !POPUPS[type]) return null;
  const cfg = POPUPS[type];

  const onPrimary = () => {
    onDismiss();
    if (cfg.target === 'credits') {
      onCredits();
    } else {
      navigate('/dashboard/billing');
    }
  };

  const onSecondary = () => {
    if (type === 'credits_exhausted') {
      onDismiss();
      navigate('/dashboard/billing');
    } else {
      onDismiss();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="upgrade-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
        onClick={cfg.secondary ? onDismiss : undefined}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 16 }}
          transition={{ type: 'spring', damping: 28, stiffness: 350 }}
          className="w-full max-w-sm rounded-2xl overflow-hidden"
          style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
          onClick={e => e.stopPropagation()}
          data-testid="upgrade-popup"
        >
          {/* Accent bar */}
          <div className="h-1" style={{ background: cfg.target === 'pro' ? 'linear-gradient(90deg, #c8f060, #22d3ee)' : cfg.target === 'credits' ? 'linear-gradient(90deg, #f59e0b, #c8f060)' : '#c8f060' }} />

          <div className="p-6">
            {/* Close */}
            {cfg.secondary && (
              <button onClick={onDismiss} className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.3)' }}>
                <X size={16} />
              </button>
            )}

            {/* Icon */}
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: cfg.target === 'credits' ? 'rgba(245,158,11,0.1)' : 'rgba(200,240,96,0.08)' }}>
              {cfg.target === 'credits' ? <Coins size={24} style={{ color: '#f59e0b' }} /> : cfg.target === 'pro' ? <Rocket size={24} style={{ color: '#22d3ee' }} /> : <Sparkle size={24} weight="fill" style={{ color: '#c8f060' }} />}
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#f5f0e8', fontFamily: "'DM Sans'" }}>
              {cfg.title}
            </h3>

            {/* Text */}
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(245,240,232,0.5)' }}>
              {cfg.text.split('\n\n').map((p, i) => <span key={i}>{p}<br /><br /></span>)}
            </p>

            {/* Buttons */}
            <button onClick={onPrimary} data-testid="upgrade-popup-primary"
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: '#c8f060', color: '#080808' }}>
              {cfg.primary}
            </button>

            {cfg.secondary && (
              <button onClick={onSecondary} data-testid="upgrade-popup-secondary"
                className="w-full py-2 mt-2 text-xs font-medium text-center"
                style={{ color: 'rgba(245,240,232,0.35)' }}>
                {cfg.secondary}
              </button>
            )}

            {/* Trust text */}
            <p className="text-[10px] text-center mt-3" style={{ color: 'rgba(245,240,232,0.2)' }}>
              {cfg.target === 'credits' ? 'Credits added instantly' : 'Upgrade in seconds \u00b7 No hidden fees \u00b7 Cancel anytime'}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─── Credit Purchase Modal ─── */
function CreditPurchaseModal({ open, onClose }) {
  const [purchasing, setPurchasing] = useState(null);
  const packs = [
    { id: 'credits_20', name: 'Starter Pack', credits: 20, price: 9.99, perCredit: '0.50' },
    { id: 'credits_60', name: 'Creator Pack', credits: 60, price: 24.99, perCredit: '0.42', popular: true },
    { id: 'credits_150', name: 'Pro Pack', credits: 150, price: 49.99, perCredit: '0.33' },
    { id: 'credits_350', name: 'Studio Pack', credits: 350, price: 89.99, perCredit: '0.26', best: true },
  ];

  const handleBuy = async (pack) => {
    setPurchasing(pack.id);
    try {
      // Fire InitiateCheckout before redirect
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'InitiateCheckout', { value: pack.price, currency: 'USD', content_name: pack.name });
      }
      const res = await axios.post(`${API}/api/payments/checkout`, {
        package_id: pack.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
      setPurchasing(null);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="credit-modal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 16 }}
          className="w-full max-w-md rounded-2xl overflow-hidden"
          style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
          onClick={e => e.stopPropagation()}
          data-testid="credit-purchase-modal"
        >
          <div className="h-1" style={{ background: 'linear-gradient(90deg, #f59e0b, #c8f060)' }} />
          <div className="p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: '#f5f0e8' }}>Buy Credits</h3>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.35)' }}>Credits never expire. Use for video, carousel, characters & more.</p>
              </div>
              <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.3)' }}>
                <X size={16} />
              </button>
            </div>

            <div className="space-y-2">
              {packs.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleBuy(p)}
                  disabled={purchasing === p.id}
                  data-testid={`buy-${p.id}`}
                  className="w-full flex items-center justify-between p-4 rounded-xl transition-all hover:scale-[1.01]"
                  style={{
                    background: p.best ? 'rgba(200,240,96,0.06)' : p.popular ? 'rgba(200,240,96,0.03)' : 'rgba(255,255,255,0.02)',
                    border: p.best ? '1px solid rgba(200,240,96,0.2)' : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: p.best ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.03)' }}>
                      <Coins size={18} style={{ color: p.best ? '#c8f060' : 'rgba(245,240,232,0.4)' }} />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{p.name}</p>
                      <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>
                        {p.credits} credits &middot; ${p.perCredit}/credit
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.best && (
                      <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(200,240,96,0.1)', color: '#c8f060' }}>Best Value</span>
                    )}
                    {p.popular && !p.best && (
                      <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060' }}>Popular</span>
                    )}
                    <span className="text-sm font-bold" style={{ color: '#f5f0e8' }}>${p.price}</span>
                  </div>
                </button>
              ))}
            </div>

            <p className="text-[10px] text-center mt-4" style={{ color: 'rgba(245,240,232,0.2)' }}>
              Credits added instantly &middot; No expiration &middot; All prices in USD
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
